import type { TabViewPanelProps } from '@myosh/odin-components';
import clsx from 'clsx';
import { TabPanel, TabView as TabViewPrimeReact } from 'primereact/tabview';
import React, {
  type ReactElement,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

export type TabViewProps = {
  ref?: Ref<TabViewRef>;
  activeIndex?: number;
  onTabChange?: (index: number) => void;
  scrollable?: boolean;
  className?: string;
  children?: ReactElement<TabViewPanelProps> | Array<ReactElement<TabViewPanelProps>>;
  id?: string;
  headerClassName?: string;
  hideBorder?: boolean;
  scrollToNextTab?: boolean;
  renderActiveOnly?: boolean;
  onKeyDownCapture?: (e: React.KeyboardEvent) => void;
  scrollSyncKey?: string | boolean; // Optional key to trigger scroll synchronization when activeIndex changes
};

export interface TabViewRef {
  setActiveTab: (index: number) => void;
}

const TabView = (props: TabViewProps) => {
  const {
    ref,
    activeIndex: controlledActiveIndex = 0,
    scrollable = true,
    className = '',
    children,
    onTabChange,
    id,
    headerClassName,
    hideBorder = false,
    scrollToNextTab = false,
    renderActiveOnly = true,
    onKeyDownCapture,
    scrollSyncKey,
  } = props;
  const [activeIndex, setActiveIndex] = useState<number>(controlledActiveIndex);
  const tabViewRef = React.useRef<TabViewPrimeReact>(null);
  const activeScrollIndexRef = React.useRef<number>(0);
  const tabsRef = React.useRef<NodeListOf<HTMLElement> | null>(null);
  const pendingFrameRef = React.useRef<number | null>(null);

  const scrollTabToContainerCenter = useCallback((navContainer: HTMLElement, targetTab: HTMLElement) => {
    const maxScrollLeft = Math.max(0, navContainer.scrollWidth - navContainer.clientWidth);
    const centeredLeft = targetTab.offsetLeft - (navContainer.clientWidth - targetTab.offsetWidth) / 2;
    const left = Math.max(0, Math.min(centeredLeft, maxScrollLeft));

    navContainer.scrollTo({
      left,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    setActiveIndex(controlledActiveIndex);

    requestAnimationFrame(() => {
      const tabViewElement = tabViewRef?.current?.getElement();
      if (!tabViewElement) return;

      const navContainer = tabViewElement.querySelector('.p-tabview-nav-content') as HTMLElement | null;
      const targetTab = tabViewElement.querySelectorAll('.p-tabview-nav li')[controlledActiveIndex] as HTMLElement;

      if (!navContainer || !targetTab) return;

      scrollTabToContainerCenter(navContainer, targetTab);
      activeScrollIndexRef.current = controlledActiveIndex;
    });
  }, [controlledActiveIndex, scrollSyncKey, scrollTabToContainerCenter]);

  const tabPanels = useMemo(() => {
    // Reset tabs cache when children change
    tabsRef.current = null;

    return React.Children.map(children, (tab) => {
      if (!React.isValidElement(tab)) {
        return null;
      }

      return (
        <TabPanel
          className={id}
          contentClassName={tab.props.panelStyles}
          header={tab.props.header}
          headerTemplate={tab.props.headerTemplate}
          headerClassName={headerClassName}
          disabled={tab.props.disabled}
        >
          {tab.props.children}
        </TabPanel>
      );
    });
  }, [children, headerClassName, id]);
  // Cleanup pending animation frames on unmount
  useEffect(() => {
    return () => {
      if (pendingFrameRef.current !== null) {
        cancelAnimationFrame(pendingFrameRef.current);
      }
    };
  }, []);

  const updateActiveIndex = (index: number) => {
    setActiveIndex(index);
  };

  useImperativeHandle(ref, () => ({
    setActiveTab: (index: number) => {
      updateActiveIndex(index);
    },
  }));

  const tabViewClassName = clsx('odin tab-view-component', className, {
    'hide-border': hideBorder,
  });

  const getCurrentVisibleTabIndex = useCallback((navContainer: HTMLElement, tabs: NodeListOf<HTMLElement>): number => {
    const containerLeft = navContainer.scrollLeft;
    const containerRight = containerLeft + navContainer.clientWidth;

    // Find the first tab whose left edge is within the visible area.
    for (let i = 0; i < tabs.length; i++) {
      const tabLeft = tabs[i].offsetLeft;

      if (tabLeft >= containerLeft - 1 && tabLeft < containerRight) {
        return i;
      }
    }
    return 0;
  }, []);

  const onTabChangeHandler = useCallback(
    (event: { index: number }) => {
      const index = event.index;
      if (activeIndex !== index) {
        setActiveIndex(index);
        onTabChange?.(index);
      }
    },
    [activeIndex, onTabChange]
  );

  const handleScrollButtonClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, direction: 'left' | 'right') => {
      if (!scrollToNextTab) return;

      e.preventDefault();
      e.stopPropagation();

      // Cancel any pending frame
      if (pendingFrameRef.current !== null) {
        cancelAnimationFrame(pendingFrameRef.current);
      }

      pendingFrameRef.current = requestAnimationFrame(() => {
        pendingFrameRef.current = null; // Clear the ref when frame executes

        const tabViewElement = tabViewRef.current?.getElement();
        if (!tabViewElement) return;

        const navContainer = tabViewElement.querySelector('.p-tabview-nav-content') as HTMLElement;

        if (!navContainer) return;

        // Cache tabs on first access or when cache is invalidated and prevent race conditions
        if (!tabsRef.current || tabsRef.current.length !== navContainer.children.length) {
          tabsRef.current = navContainer.querySelectorAll('li[data-position]') as NodeListOf<HTMLElement>;
        }

        const tabs = tabsRef.current;
        if (!tabs.length) return;

        const prevButton = tabViewElement.querySelector('.p-tabview-nav-prev') as HTMLElement | null;
        const leftChevronWidth = prevButton && prevButton.offsetParent !== null ? prevButton.offsetWidth : 24;

        const currentIndex = getCurrentVisibleTabIndex(navContainer, tabs);

        const nextIndex =
          direction === 'left' ? Math.max(0, currentIndex - 1) : Math.min(tabs.length - 1, currentIndex + 1);

        const targetTab = tabs[nextIndex];
        if (!targetTab) return;

        const left = Math.max(0, targetTab.offsetLeft - leftChevronWidth);

        navContainer.scrollTo({
          left,
          behavior: 'smooth',
        });
      });
    },
    [getCurrentVisibleTabIndex, scrollToNextTab]
  );

  return (
    <TabViewPrimeReact
      id={id}
      activeIndex={activeIndex}
      scrollable={scrollable}
      renderActiveOnly={renderActiveOnly}
      pt={{
        inkbar: { style: { display: 'none' } },
        nextButton: {
          onClickCapture: (e) => handleScrollButtonClick(e, 'right'),
        },
        prevButton: {
          onClickCapture: (e) => handleScrollButtonClick(e, 'left'),
        },
      }}
      onTabChange={onTabChangeHandler}
      className={tabViewClassName}
      ref={tabViewRef}
      onKeyDownCapture={onKeyDownCapture}
    >
      {tabPanels}
    </TabViewPrimeReact>
  );
};

export default TabView;
